import { API_URL } from '../constants';

/**
 * Memory API utilities for GDPR compliance
 * Handles viewing, deleting, and exporting user memory data
 * Also handles pending fact review for context memory
 */

// ============ PENDING FACTS (Context Memory Review) ============

/**
 * Get pending facts awaiting user review
 * @param {string} accessToken - Kinde access token
 * @param {number} limit - Max facts to return (default 20)
 * @returns {Promise<object>} { facts: [], total: number }
 */
export async function getPendingFacts(accessToken, limit = 20) {
  const response = await fetch(`${API_URL}/api/user/facts/pending?limit=${limit}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch pending facts');
  }
  
  return response.json();
}

/**
 * Get count of pending facts (for badge)
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} { count: number }
 */
export async function getPendingFactsCount(accessToken) {
  const response = await fetch(`${API_URL}/api/user/facts/pending/count`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch pending count');
  }
  
  return response.json();
}

/**
 * Confirm a pending fact (moves to current status)
 * @param {string} factId - ID of fact to confirm
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Success response
 */
export async function confirmFact(factId, accessToken) {
  const response = await fetch(`${API_URL}/api/user/facts/${factId}/confirm`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to confirm fact');
  }
  
  return response.json();
}

/**
 * Reject/delete a pending fact
 * @param {string} factId - ID of fact to reject
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Success response
 */
export async function rejectFact(factId, accessToken) {
  const response = await fetch(`${API_URL}/api/user/facts/${factId}/reject`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject fact');
  }
  
  return response.json();
}

/**
 * Edit a pending fact before confirming
 * @param {string} factId - ID of fact to edit
 * @param {object} updates - { content?, category?, domains?, privacy_level? }
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Updated fact
 */
export async function editFact(factId, updates, accessToken) {
  const response = await fetch(`${API_URL}/api/user/facts/${factId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to edit fact');
  }
  
  return response.json();
}

// ============ TEMPORAL EXPIRY ============

/**
 * Trigger expiry check for stale temporal facts
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} { expired: number }
 */
export async function expireStaleFacts(accessToken) {
  const response = await fetch(`${API_URL}/api/user/facts/expire`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to expire facts');
  }
  
  return response.json();
}

/**
 * Get expired facts
 * @param {string} accessToken - Kinde access token
 * @param {number} limit - Max facts to return (default 50)
 * @returns {Promise<object>} { facts: [], count: number }
 */
export async function getExpiredFacts(accessToken, limit = 50) {
  const response = await fetch(`${API_URL}/api/user/facts/expired?limit=${limit}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch expired facts');
  }
  
  return response.json();
}

/**
 * Restore an expired fact back to current status
 * @param {string} factId - ID of fact to restore
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Restored fact
 */
export async function restoreFact(factId, accessToken) {
  const response = await fetch(`${API_URL}/api/user/facts/${factId}/restore`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to restore fact');
  }
  
  return response.json();
}

/**
 * Get fact history for a category
 * @param {string} category - Fact category (e.g., 'identity', 'currentWork')
 * @param {string} accessToken - Kinde access token
 * @param {string} extractType - Optional extract type filter
 * @returns {Promise<object>} { current, history, timeline, total }
 */
export async function getFactHistory(category, accessToken, extractType = null) {
  let url = `${API_URL}/api/user/facts/history/${category}`;
  if (extractType) {
    url += `?extractType=${encodeURIComponent(extractType)}`;
  }
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get fact history');
  }
  
  return response.json();
}

/**
 * Get current facts with their previous values
 * @param {string} accessToken - Kinde access token
 * @param {Array} domains - Optional domain filter
 * @param {number} limit - Max facts to return
 * @returns {Promise<object>} { facts, count }
 */
export async function getFactsWithHistory(accessToken, domains = [], limit = 20) {
  let url = `${API_URL}/api/user/facts/with-history?limit=${limit}`;
  if (domains.length > 0) {
    url += `&domains=${domains.join(',')}`;
  }
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get facts with history');
  }
  
  return response.json();
}

// ============ GDPR MEMORY ENDPOINTS ============

/**
 * Get all stored facts for current user
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} User's memory data
 */
export async function getMemory(accessToken) {
  const response = await fetch(`${API_URL}/api/user/memory`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch memory');
  }
  
  return response.json();
}

/**
 * Delete a specific fact
 * @param {string} factId - ID of fact to delete
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Success response
 */
export async function deleteFact(factId, accessToken) {
  const response = await fetch(`${API_URL}/api/user/memory/${factId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete fact');
  }
  
  return response.json();
}

/**
 * Delete all user data (requires confirmation)
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Success response
 */
export async function deleteAllMemory(accessToken) {
  const response = await fetch(`${API_URL}/api/user/memory`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ confirmed: true })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete all data');
  }
  
  return response.json();
}

/**
 * Export all user data as JSON
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<void>} Triggers file download
 */
export async function exportMemory(accessToken) {
  const response = await fetch(`${API_URL}/api/user/memory/export`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to export data');
  }
  
  const data = await response.json();
  
  // Trigger download
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'mabel-memory-export.json';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Get user's preferences (privacy level, display name, consent)
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} User preferences
 */
export async function getPreferences(accessToken) {
  const response = await fetch(`${API_URL}/api/user/preferences`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch preferences');
  }
  
  return response.json();
}

// Alias for backwards compatibility
export const getPrivacyPreferences = getPreferences;

/**
 * Update user preferences (display name and/or privacy level)
 * @param {object} updates - { display_name?, privacy_level? }
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Updated preferences
 */
export async function updatePreferences(updates, accessToken) {
  const response = await fetch(`${API_URL}/api/user/preferences`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update preferences');
  }
  
  return response.json();
}

/**
 * Update privacy level
 * @param {string} privacyLevel - WORK_ONLY | ENHANCED | FULL
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Updated preferences
 */
export async function updatePrivacyLevel(privacyLevel, accessToken) {
  const response = await fetch(`${API_URL}/api/user/preferences`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ privacy_level: privacyLevel })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update privacy level');
  }
  
  return response.json();
}

/**
 * Record user consent
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Success response
 */
export async function recordConsent(accessToken) {
  const response = await fetch(`${API_URL}/api/user/consent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      agreed: true,
      timestamp: new Date().toISOString(),
      version: '1.0'
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to record consent');
  }
  
  return response.json();
}

// ============ ONBOARDING ============

/**
 * Check if user needs onboarding
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} { needs_onboarding, onboarding_complete, display_name }
 */
export async function getOnboardingStatus(accessToken) {
  const response = await fetch(`${API_URL}/api/user/onboarding`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get onboarding status');
  }
  
  return response.json();
}

/**
 * Mark onboarding as complete
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Success response
 */
export async function completeOnboarding(accessToken) {
  const response = await fetch(`${API_URL}/api/user/onboarding/complete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to complete onboarding');
  }
  
  return response.json();
}

/**
 * Get available privacy levels (no auth needed)
 * @returns {Promise<object>} Privacy level definitions
 */
export async function getPrivacyLevels() {
  const response = await fetch(`${API_URL}/api/user/privacy-levels`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch privacy levels');
  }
  
  return response.json();
}
